import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useDriver } from "../../lib/driverContext";
import { getDriverBookings } from "../../lib/driverApi";
import type { DriverBooking } from "../../lib/driverApi";
import { assetUrl } from "../../lib/api";
import { Card, PageTitle, StatusBadge } from "../../components/company/ui";
import { cn, daysBetween, longDate, shortDateTime, today } from "../../lib/format";
import { useT } from "../../i18n";

const DOT: Record<DriverBooking["status"], string> = {
  pending: "bg-[#C58A1B]",
  documents_required: "bg-[#C58A1B]",
  confirmed: "bg-brand",
  active: "bg-tone-green-fg",
  completed: "bg-faint",
  declined: "bg-tone-red-fg",
  cancelled: "bg-faint",
  expired: "bg-faint",
};
// The booking worth following first: running, then confirmed, then waiting
const TRACK_ORDER: DriverBooking["status"][] = ["active", "confirmed", "documents_required", "pending"];

/** Driver dashboard: VTC card reminder, booking to track, recent activity and counters. */
export default function DriverHome() {
  const t = useT();
  const h = t.driverArea.home;
  const dh = t.driverPages.home;
  const { driver, summary } = useDriver();
  const [bookings, setBookings] = useState<DriverBooking[] | null>(null);

  useEffect(() => {
    getDriverBookings().then(setBookings, () => setBookings([]));
  }, []);

  if (!driver) return <p className="text-sm text-muted">{t.common.loading}</p>;

  const docs = driver.documents;
  const uploaded = docs.filter((d) => d.status !== "missing").length;
  const approved = docs.filter((d) => d.status === "approved").length;
  const needsAction = docs.some((d) => d.status === "missing" || d.status === "rejected");

  // VTC card reminder: expired, or expiring within 60 days
  const expiry = driver.vtcCardExpiry?.slice(0, 10);
  const expiryDays = expiry ? daysBetween(today(), expiry) : Infinity;
  const vtcBanner = expiry && expiryDays < 60 ? (expiryDays < 0 ? dh.vtcExpired : dh.vtcExpiring)(longDate(expiry)) : null;

  const track = bookings
    ?.filter((b) => TRACK_ORDER.includes(b.status))
    .sort((a, b) => TRACK_ORDER.indexOf(a.status) - TRACK_ORDER.indexOf(b.status) || a.start.localeCompare(b.start))[0];
  const activity = [...(bookings ?? [])].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)).slice(0, 5);
  const trackText = (b: DriverBooking) =>
    b.status === "pending"
      ? dh.waiting
      : b.status === "documents_required"
        ? dh.docsRequired
        : b.status === "confirmed"
          ? dh.confirmedText(b.vehicle?.code ?? "")
          : dh.activeText;

  const stats: [string | number, string][] = [
    [summary?.pendingRequests ?? "–", h.stats.pending],
    [summary?.confirmedOrActive ?? "–", h.stats.confirmed],
    [`${approved} / ${docs.length}`, h.stats.approvedDocs],
    [summary?.favourites ?? "–", h.stats.favourites],
  ];

  return (
    <>
      <PageTitle className="mb-1">{h.hello(driver.firstName)}</PageTitle>
      <p className="m-0 mb-5 text-sm text-muted">{h.intro}</p>

      {vtcBanner && (
        <div className="mb-4 flex flex-wrap items-center gap-3 rounded-card bg-tone-amber-bg px-4 py-3.5 text-sm text-[#5A3A00]">
          <span className="min-w-55 flex-1">{vtcBanner}</span>
          <Link to="/driver/documents" className="text-[13px] font-medium text-[#5A3A00] underline">
            {dh.manageDocs}
          </Link>
        </div>
      )}

      {(needsAction || uploaded < docs.length) && (
        <Card className="mb-4 flex flex-wrap items-center gap-4">
          <div className="min-w-60 flex-1">
            <h2 className="m-0 text-base font-medium">{needsAction ? h.completeTitle : h.sentTitle}</h2>
            <p className="m-0 mt-1 text-[13px] text-muted">{needsAction ? h.completeText(docs.length) : h.sentText}</p>
            <div className="mt-3 h-1 max-w-75 overflow-hidden rounded-sm bg-line-strong">
              <div className="h-full bg-brand" style={{ width: `${(uploaded / docs.length) * 100}%` }} />
            </div>
            <p className="m-0 mt-1.5 text-xs text-muted">{h.uploaded(uploaded, docs.length)}</p>
          </div>
          {needsAction && (
            <Link
              to="/driver/documents"
              className="flex h-10 items-center rounded bg-brand px-4.5 text-sm font-medium text-white no-underline hover:bg-brand-dark"
            >
              {h.uploadDocs}
            </Link>
          )}
        </Card>
      )}

      <div className="mb-4 grid items-start gap-4 md:grid-cols-2">
        <Card>
          <div className="mb-3 text-[13px] text-muted">{h.trackTitle}</div>
          {bookings === null ? (
            <div className="h-24 animate-pulse rounded bg-surface" />
          ) : track ? (
            <>
              <div className="flex gap-3.5">
                <div className="aspect-16/10 w-24 shrink-0 overflow-hidden rounded bg-linear-to-b from-surface to-[#E2E3E4]">
                  {track.listing?.photo && <img src={assetUrl(track.listing.photo)} alt="" className="h-full w-full object-cover" />}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <b className="text-base font-medium">
                      {track.listing ? `${track.listing.brand} ${track.listing.model}` : track.ref}
                    </b>
                    <StatusBadge kind="booking" status={track.status} />
                  </div>
                  <div className="text-[13px] text-muted">{track.company?.name}</div>
                  <div className="mt-0.5 text-[13px]">
                    {longDate(track.start)} → {longDate(track.end)}
                  </div>
                </div>
              </div>
              <p className="m-0 mt-3 text-[13px] text-ink-soft">{trackText(track)}</p>
              <Link
                to={`/driver/bookings/${track.ref}`}
                className="mt-3 flex h-10 items-center justify-center rounded bg-surface text-sm font-medium text-ink no-underline hover:bg-surface-hover"
              >
                {dh.viewDetails}
              </Link>
            </>
          ) : (
            <>
              <p className="m-0 mb-3 text-sm">{h.noRequest}</p>
              <Link to="/vehicles" className="text-sm text-brand-link">
                {h.findVtc}
              </Link>
            </>
          )}
        </Card>
        <Card>
          <div className="mb-3 text-[13px] text-muted">{h.activity}</div>
          {activity.length ? (
            <ul className="m-0 list-none p-0">
              {activity.map((b) => (
                <li key={b.id} className="border-b border-line-strong py-2.5 last:border-b-0">
                  <Link to={`/driver/bookings/${b.ref}`} className="flex gap-2.5 text-ink no-underline">
                    <span className={cn("mt-1.5 h-2 w-2 shrink-0 rounded-full", DOT[b.status])} />
                    <span>
                      <span className="text-[13px]">
                        {dh.activityLine(
                          t.company.status.booking[b.status],
                          b.listing ? `${b.listing.brand} ${b.listing.model}` : b.ref,
                        )}
                      </span>
                      <span className="block text-xs text-muted">{shortDateTime(b.updatedAt)}</span>
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <p className="m-0 text-[13px] text-faint">{h.noActivity}</p>
          )}
        </Card>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {stats.map(([value, label]) => (
          <Card key={label}>
            <div className="text-[26px] font-medium">{value}</div>
            <div className="text-[13px] text-muted">{label}</div>
          </Card>
        ))}
      </div>
    </>
  );
}
